/* eslint-disable react/no-danger */
import { Star } from '@mui/icons-material';
import {
  Typography, Avatar, Box, Divider,
} from '@mui/material';

import { BookIcon, ProfileUserIcon } from 'assets/icons';
import { selectAuthUser } from 'base/auth/selectors';
import { WhiteBgCard, SecondaryBadge } from 'components';
import { selectSessionsResult } from 'pages/sessions/components/sessions-create/selector';
import { useSelector } from 'react-redux';
import { decimalFormat } from 'utils/formats';
import DOMPurify from 'isomorphic-dompurify';
import { getAvatarLetters, getUserName } from 'utils/common-utils';
import StreamingPlayer from 'components/organisms/streaming-player';

const GeneralInformation = () => {
  const authUser = useSelector(selectAuthUser);
  const { total: noOfSessions } = useSelector(selectSessionsResult);

  return (
    <WhiteBgCard sx={{ mb: 3 }}>

      <div className="row mb-4">

        <div className="col-12 d-md-none mb-3">
          <Typography
            variant="subtitle2"
            sx={{ color: 'text.secondary' }}
          >
            {`${authUser.attributes.accountType} PROFILE`}
          </Typography>
        </div>

        <div className="col-auto">
          <Avatar
            sx={{
              width: {
                xs: 50,
                md: 80,
              },
              height: {
                xs: 50,
                md: 80,
              },
              mb: 2,
            }}
            alt={getAvatarLetters(authUser)}
            src={authUser?.profileImgUrl}
          />

        </div>
        <div className="col pt-1">
          <div className="d-flex justify-content-between mb-2 lh-1">
            <Typography variant="h5" sx={{ color: 'text.primary' }}>
              {getUserName(authUser)}
            </Typography>
            <Typography className="d-none d-md-block" variant="subtitle2" sx={{ color: 'text.secondary' }}>
              {`${authUser.attributes.accountType} PROFILE`}
            </Typography>
          </div>
          <Typography className="lh-1 mb-3" variant="body1">
            {authUser.attributes.category}
          </Typography>

          <div className="d-flex align-items-center">
            <Box
              className="rounded-circle d-flex justify-content-center align-items-center me-2"
              sx={{ backgroundColor: 'secondary.light', width: 24, height: 24 }}
            >
              <Star
                sx={{
                  fontSize: 14, color: 'secondary.dark',
                }}
              />

            </Box>
            <Typography className="me-2" variant="body2">
              {decimalFormat(authUser.attributes.rating, 1)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {`(${authUser.attributes.noOfRatings} ratings)`}
            </Typography>
          </div>

        </div>
      </div>

      <div className="mb-4 htmlDescription" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(authUser?.bio && authUser.bio.length > 0 ? authUser.bio : '') }} />

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }} className="row gy-3">
        <div className="col-auto">
          <SecondaryBadge sx={{ py: 1 }} icon={<BookIcon sx={{ mr: 1, fontSize: 20 }} />}>
            {`${noOfSessions} sessions in total`}
          </SecondaryBadge>
        </div>

        <div className="col-auto">
          <SecondaryBadge sx={{ py: 1 }} icon={<ProfileUserIcon sx={{ mr: 1, fontSize: 20 }} />}>
            {`${authUser.attributes.users.length} active users`}
          </SecondaryBadge>
        </div>

        <div className="col-auto">
          <SecondaryBadge sx={{ py: 1 }} icon={<ProfileUserIcon sx={{ mr: 1, fontSize: 20 }} />}>
            {`${authUser.attributes.followersCount} followers`}
          </SecondaryBadge>
        </div>

      </Box>

      <div className="row">
        <div className="col-sm-6 col-md-4">
          {authUser.attributes.introVideoUrl ? (
            <div className="d-block">
              <StreamingPlayer
                src={authUser?.attributes?.introVideoUrl || ''}
                openInModal
                sx={{
                  height: '100%',
                  width: '100%',
                  objectFit: 'contain',
                  display: 'flex',
                  alignItems: 'stretch',
                  borderRadius: '6px',
                  overflow: 'hidden',
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                Intro video
              </Typography>
            </div>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              Intro video not uploaded
            </Typography>
          )}
        </div>
        <div className="col-sm-6 col-md-4">
          {authUser.attributes.thankVideoUrl ? (
            <div className="d-block">
              <StreamingPlayer
                src={authUser?.attributes?.thankVideoUrl || ''}
                openInModal
                sx={{
                  height: '100%',
                  width: '100%',
                  objectFit: 'contain',
                  display: 'flex',
                  alignItems: 'stretch',
                  borderRadius: '6px',
                  overflow: 'hidden',
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                Thank you video
              </Typography>
            </div>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              Thank you video not uploaded
            </Typography>
          )}
        </div>
        <div className="col-sm-6 col-md-4">
          {authUser.attributes.rateVideoUrl ? (
            <div className="d-block">
              <StreamingPlayer
                src={authUser?.attributes?.rateVideoUrl || ''}
                openInModal
                sx={{
                  height: '100%',
                  width: '100%',
                  objectFit: 'contain',
                  display: 'flex',
                  alignItems: 'stretch',
                  borderRadius: '6px',
                  overflow: 'hidden',
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                Rating video
              </Typography>
            </div>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              Rating video not uploaded
            </Typography>
          )}
        </div>
      </div>

    </WhiteBgCard>
  );
};

export default GeneralInformation;
